import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, X } from "lucide-react";
import { Reveal, Kicker } from "./Reveal";
import { VIDEOS } from "../../data/site";
import { getLenis } from "../../lib/scroll";

export const VideoGallery = () => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && setActive(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    const lenis = getLenis();
    if (active) lenis?.stop();
    else lenis?.start();
  }, [active]);

  return (
    <section
      id="videos"
      data-testid="video-gallery-section"
      className="border-t border-black/5 bg-white py-24 md:py-36"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="flex flex-col justify-between gap-8 md:flex-row md:items-end">
          <div>
            <Reveal>
              <Kicker index="04" label="Video" />
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-6 font-display text-5xl font-bold uppercase tracking-tight text-ink md:text-6xl">
                Our Work <span className="text-gold-shine">in Action</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.15}>
            <p className="max-w-sm text-sm leading-relaxed text-fog">
              Real footage from our sites — no staging, no stock. Watch the
              team at work and the finishes we hand over.
            </p>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-6 md:grid-cols-2">
          {VIDEOS.map((video, i) => (
            <Reveal
              key={video.tag}
              delay={(i % 2) * 0.1}
              className={i === 0 ? "md:col-span-2" : ""}
            >
              <button
                data-testid={`video-card-${i + 1}`}
                onClick={() => setActive(video)}
                className="group relative block aspect-video w-full cursor-pointer overflow-hidden border border-black/10 bg-ink text-left"
              >
                <video
                  src={`${video.src}#t=0.5`}
                  preload="auto"
                  muted
                  playsInline
                  className="h-full w-full object-cover opacity-90 transition-all duration-700 group-hover:scale-[1.03] group-hover:opacity-100"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/85 via-transparent to-white/10" />
                <span className="absolute left-1/2 top-1/2 grid h-16 w-16 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-gold-shine transition-transform duration-500 group-hover:scale-110">
                  <Play className="ml-1 h-6 w-6 fill-ink text-ink" />
                </span>
                <span className="absolute inset-x-0 bottom-0 flex items-end justify-between p-5 md:p-6">
                  <span>
                    <span className="block text-[11px] uppercase tracking-[0.3em] text-bronze">
                      {video.tag}
                    </span>
                    <span className="mt-1 block font-display text-2xl font-bold uppercase tracking-wide text-ink md:text-3xl">
                      {video.title}
                    </span>
                  </span>
                  <span className="hidden text-[11px] uppercase tracking-[0.25em] text-black/50 md:block">
                    Tap to play
                  </span>
                </span>
              </button>
            </Reveal>
          ))}
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <motion.div
            data-testid="video-lightbox"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={() => setActive(null)}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 p-4 backdrop-blur-sm md:p-10"
          >
            <button
              data-testid="video-lightbox-close"
              onClick={() => setActive(null)}
              aria-label="Close video"
              className="absolute right-5 top-5 grid h-12 w-12 place-items-center border border-bronze/60 text-bronze transition-colors duration-300 hover:bg-bronze hover:text-ink"
            >
              <X className="h-5 w-5" />
            </button>
            <motion.div
              initial={{ scale: 0.94, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.94, y: 20 }}
              transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-5xl"
            >
              <video
                data-testid="video-lightbox-player"
                src={active.src}
                controls
                autoPlay
                playsInline
                className="max-h-[78vh] w-full bg-black"
              />
              <div className="mt-4 flex items-center justify-between text-[11px] uppercase tracking-[0.25em]">
                <span className="text-bronze">{active.title}</span>
                <span className="text-bronze/70">Sunny's Construction Ltd</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};
